import {
  mergeAppSettings,
  normalizeAppSettings
} from "../settings/appSettings.js";
import {
  archiveProfile,
  archiveProfileYear,
  createProfile,
  createProfileYear,
  deleteProfileYear,
  getProfileSettings,
  listProfiles,
  listProfileYears,
  renameProfile,
  unarchiveProfileYear,
  updateProfileSettings
} from "./walletWatchProfiles.js";
import {
  exportActiveProfileBackup,
  restoreProfileBackup
} from "./walletWatchBackup.js";
import {
  listBudgetCategoryOptions,
  listBudgetRows,
  listExpenseRows,
  updateBudgetRows,
  updateExpenseRows
} from "./walletWatchBudgetExpenses.js";
import {
  countStore,
  CURRENT_YEAR,
  DB_NAME,
  DB_VERSION,
  DEFAULT_PROFILE_ID,
  deleteHandleValue,
  deleteMetaValue,
  ensureSeedData,
  getHandleValue,
  getMetaValue,
  openDatabase,
  readAllFromStore,
  requestToPromise,
  setHandleValue,
  setMetaValues,
  STORE_APP_META,
  STORE_PROFILES,
  transactionDone
} from "./walletWatchDbCore.js";

export async function initializeWalletWatchDb() {
  await openDatabase();
  await ensureSeedData();
}

export async function getActiveBudgetScope() {
  await ensureSeedData();
  const [profileId, year] = await Promise.all([
    getMetaValue("activeProfileId"),
    getMetaValue("activeYear")
  ]);

  return {
    profileId: profileId ?? DEFAULT_PROFILE_ID,
    year: year ?? CURRENT_YEAR
  };
}

export async function getAppSettings() {
  const [language, theme] = await Promise.all([
    getMetaValue("language"),
    getMetaValue("theme")
  ]);
  return normalizeAppSettings({ language, theme });
}

export async function updateAppSettings(nextSettings) {
  const currentSettings = await getAppSettings();
  const settings = mergeAppSettings(currentSettings, nextSettings);
  await setMetaValues([
    ["language", settings.language],
    ["theme", settings.theme]
  ]);
  return settings;
}

export async function getStoredAppMetaValue(key) {
  return getMetaValue(key);
}

export async function setStoredAppMetaValue(key, value) {
  await setMetaValues([[key, value]]);
}

export async function clearStoredAppMetaValue(key) {
  await deleteMetaValue(key);
}

export async function getStoredAppHandle(key) {
  return getHandleValue(key);
}

export async function setStoredAppHandle(key, handle) {
  await setHandleValue(key, handle);
}

export async function clearStoredAppHandle(key) {
  await deleteHandleValue(key);
}

export { purgeWalletWatchDb, subscribeProfileDataChanges } from "./walletWatchDbCore.js";
export { exportActiveProfileBackup, restoreProfileBackup } from "./walletWatchBackup.js";

export {
  archiveProfile,
  archiveProfileYear,
  createProfile,
  createProfileYear,
  deleteProfileYear,
  getProfileSettings,
  listProfiles,
  listProfileYears,
  renameProfile,
  setActiveBudgetScope,
  unarchiveProfileYear,
  updateProfileSettings
} from "./walletWatchProfiles.js";

export {
  listBudgetCategoryOptions,
  listBudgetRows,
  listExpenseRows,
  updateBudgetRows,
  updateExpenseRows
} from "./walletWatchBudgetExpenses.js";

export async function getDatabaseSummary() {
  await ensureSeedData();
  const db = await openDatabase();
  const storeNames = Array.from(db.objectStoreNames);
  const transaction = db.transaction(storeNames, "readonly");
  const appMetaStore = transaction.objectStore(STORE_APP_META);
  const profilesStore = transaction.objectStore(STORE_PROFILES);
  const [activeProfileIdRecord, activeYearRecord, languageRecord, profiles, storeSummaries] = await Promise.all([
    requestToPromise(appMetaStore.get("activeProfileId")),
    requestToPromise(appMetaStore.get("activeYear")),
    requestToPromise(appMetaStore.get("language")),
    readAllFromStore(profilesStore),
    Promise.all(storeNames.map(async (storeName) => ({
      name: storeName,
      count: await countStore(transaction.objectStore(storeName))
    })))
  ]);
  await transactionDone(transaction);
  const activeProfileId = activeProfileIdRecord?.value ?? DEFAULT_PROFILE_ID;
  const activeProfile = profiles.find((profile) => profile.id === activeProfileId) ?? null;

  return {
    name: DB_NAME,
    version: DB_VERSION,
    activeProfileId,
    activeProfileName: String(activeProfile?.name ?? activeProfileId),
    activeYear: activeYearRecord?.value ?? CURRENT_YEAR,
    language: languageRecord?.value ?? null,
    stores: storeSummaries
  };
}
